import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiEye, FiEyeOff, FiArrowRight } from 'react-icons/fi';
import api from '../../utils/api';
import { BRAND, Kicker, inputStyle, labelStyle, buttonStyle } from '../common/ui';

function getErrorMessage(err) {
  const d = err?.response?.data;
  if (Array.isArray(d?.details) && d.details.length > 0) return d.details.map(e => e.message).join('\n');
  if (typeof d?.message === 'string') return d.message;
  if (typeof d?.error === 'string') return d.error;
  return err?.message || 'Something went wrong. Try again!';
}

const input = inputStyle(false);
const label = labelStyle(false);
const primary = (loading) => ({ ...buttonStyle('gold', 'lg'), width: '100%', marginTop: 6, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.8 : 1 });

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tokenValid = /^[0-9a-fA-F]{64}$/.test(token);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 12) {
      toast.error('Password must be at least 12 characters.');
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      toast.error('Password must contain uppercase, lowercase and a digit.');
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match!");
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      toast.success('Password updated! You can now log in.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', background: BRAND.navy900, fontFamily: BRAND.font, color: BRAND.ivory }}>
      <motion.img src={BRAND.bg.school} alt="" aria-hidden initial={{ scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 16, ease: 'linear' }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%' }} />
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(7,20,45,0.72) 0%, rgba(7,20,45,0.86) 55%, rgba(7,20,45,0.97) 100%)' }} />

      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(20px,4vw,64px) 16px' }}>
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 100, damping: 18 }}
          style={{ width: '100%', maxWidth: 440, background: 'rgba(11,26,58,0.74)', border: `1px solid ${BRAND.line}`, borderRadius: 26, backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: '0 24px 70px rgba(0,0,0,0.5)', padding: 'clamp(22px,3vw,34px)' }}
        >
          <img src={BRAND.owl} alt="C.O.R.E." style={{ width: 84, margin: '0 auto 10px', display: 'block', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))' }} />
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <Kicker>C.O.R.E. for Schools</Kicker>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '8px 0 0' }}>Choose a new password</h1>
            <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.1rem', color: 'rgba(249,240,231,0.85)', margin: '4px 0 0' }}>Teacher / administrator account</p>
          </div>

          {!tokenValid ? (
            <div style={{ textAlign: 'center', padding: '8px 4px' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>🔒</div>
              <p style={{ fontWeight: 800, marginBottom: 6 }}>Invalid reset link</p>
              <p style={{ fontSize: '0.9rem', color: BRAND.muted, lineHeight: 1.5 }}>
                This link is missing or malformed. Please request a new reset email from the login page.
              </p>
            </div>
          ) : done ? (
            <div style={{ textAlign: 'center', padding: '8px 4px' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>✅</div>
              <p style={{ fontWeight: 800, marginBottom: 6 }}>Password updated!</p>
              <p style={{ fontSize: '0.9rem', color: BRAND.muted, lineHeight: 1.5, marginBottom: 16 }}>
                Your password has been changed. Log in with your new password to continue.
              </p>
              <p style={{ fontSize: '0.82rem', color: BRAND.goldLight, lineHeight: 1.5, marginBottom: 16, padding: '10px 12px', borderRadius: 12, background: 'rgba(226,184,120,0.1)', border: '1px solid rgba(226,184,120,0.3)' }}>
                If your browser or phone offers to autofill the old password at login, choose "update saved password" — the old one no longer works.
              </p>
              <motion.button
                type="button"
                onClick={() => navigate('/login')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={primary(false)}
              >
                Go to login <FiArrowRight size={18} />
              </motion.button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label style={label}>New password</label>
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="At least 12 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...input, paddingRight: 44 }}
                  disabled={loading}
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} aria-label={showPwd ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', cursor: 'pointer', color: BRAND.muted, fontSize: 18, padding: 4 }}>
                  {showPwd ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <label style={label}>Confirm new password</label>
              <input
                type={showPwd ? 'text' : 'password'}
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="Repeat the password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={{ ...input, marginBottom: 10 }}
                disabled={loading}
              />
              <p style={{ fontSize: '0.8rem', color: BRAND.muted, margin: '0 0 12px', lineHeight: 1.45 }}>
                Must be at least 12 characters with uppercase, lowercase and a digit.
              </p>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                style={primary(loading)}
              >
                {loading ? 'Updating…' : 'Set new password'}
              </motion.button>
            </form>
          )}

          <div style={{ textAlign: 'center', marginTop: 18, fontSize: '0.86rem' }}>
            <Link to="/login" style={{ fontWeight: 700, color: BRAND.goldLight }}>
              ← Back to login
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
